import {
  DOMAINES_AUTOMATISME,
  type EtatAutomatisme,
  formaterJourLong,
  jourAParis,
  LIBELLES_DOMAINE_AUTOMATISME,
} from '@admin-pmvb/core'
import { useQuery } from '@tanstack/react-query'
import { Link, type LinkProps } from '@tanstack/react-router'
import { useState } from 'react'
import { type Circuits as DonneesCircuits, ErreurApi, requetes } from '../api'
import { DiagrammeFlux, type EtatJob } from '../composants/DiagrammeFlux'
import { Icone, type NomIcone } from '../composants/Icone'
import { Vide } from '../composants/Section'
import { EntetePage, Onglets, TitreCarte } from '../composants/Ui'

// Page « Circuits » (administrateurs, et une titulaire d'espace privé sur son espace) : le diagramme de ce qui se passe (canal, règle appliquée, aboutissement,
// pour les mails, les pièces et la banque), ce qui attend un humain, et les automatismes en
// service. Relue chaque minute : c'est l'écran de l'audit quotidien.

type Periode = '1' | '7' | '30'
type Automatisme = DonneesCircuits['automatismes']['liste'][number]

const CLE_PERIODE = 'circuits:periode'

function periodeRetenue(): Periode {
  try {
    const v = window.localStorage.getItem(CLE_PERIODE)
    return v === '1' || v === '30' ? v : '7'
  } catch {
    return '7'
  }
}

function retenirPeriode(p: Periode) {
  try {
    window.localStorage.setItem(CLE_PERIODE, p)
  } catch {
    // Stockage indisponible (navigation privée) : le choix vaut pour la visite.
  }
}

const ETATS: Record<EtatAutomatisme, { libelle: string; icone: NomIcone; ton: string }> = {
  ok: { libelle: 'En service', icone: 'check_circle', ton: 'ton-ok' },
  echec: { libelle: 'En échec', icone: 'warning', ton: 'ton-alerte' },
  retard: { libelle: 'En retard', icone: 'hourglass_top', ton: 'ton-alerte' },
  jamais: { libelle: 'Pas encore passé', icone: 'radio_button_unchecked', ton: '' },
  a_la_demande: { libelle: 'À la demande', icone: 'touch_app', ton: '' },
}

/** « il y a 3 min », par rapport à l'heure du serveur (pas celle du téléphone). */
function ilYA(instant: string | null | undefined, reference: string): string {
  if (!instant) return 'jamais'
  const minutes = Math.round((Date.parse(reference) - Date.parse(instant)) / 60_000)
  if (minutes < 1) return 'à l’instant'
  if (minutes < 60) return `il y a ${minutes} min`
  const heures = Math.round(minutes / 60)
  if (heures < 48) return `il y a ${heures} h`
  return `il y a ${Math.round(heures / 24)} j`
}

function heure(instant: string) {
  return new Date(instant).toLocaleTimeString('fr-FR', {
    timeZone: 'Europe/Paris',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function Circuits() {
  const [periode, setPeriode] = useState<Periode>(periodeRetenue)
  const r = useQuery(requetes.circuits(periode))
  const jour = formaterJourLong(jourAParis(new Date()))
  return (
    <main className="circuits">
      <EntetePage surtitre={`Audit du jour · ${jour}`} titre="Circuits et automatismes" />
      <div className="circuits-barre">
        <Onglets
          valeur={periode}
          onChange={(p) => {
            setPeriode(p)
            retenirPeriode(p)
          }}
          onglets={[
            { valeur: '1', libelle: '24 h' },
            { valeur: '7', libelle: '7 jours' },
            { valeur: '30', libelle: '30 jours' },
          ]}
        />
        {r.data && (
          <span className="discret">
            Ce qui est arrivé sur la période · chiffres de {heure(r.data.le)}
          </span>
        )}
      </div>
      {r.isPending && <Vide>Chargement…</Vide>}
      {r.error && (
        <div className="alerte">
          {r.error instanceof ErreurApi && r.error.statut === 403
            ? 'Cette page est réservée aux administrateurs et aux titulaires d’un espace privé.'
            : `Chiffres indisponibles : ${r.error.message}`}
        </div>
      )}
      {r.data && <Contenu d={r.data} />}
    </main>
  )
}

function Contenu({ d }: { d: DonneesCircuits }) {
  const jobs = new Map<string, EtatJob>(
    d.automatismes.liste.map((a) => [
      a.nom,
      {
        libelle: a.libelle,
        cadence: a.cadence,
        enEchec: a.etat === 'echec' || a.etat === 'retard',
      },
    ]),
  )
  return (
    <div className="pile-large">
      <section className="carte circuits-diagramme">
        <TitreCarte icone="account_tree" titre="Ce qui se passe : mails et pièces" />
        <p className="discret">
          De gauche à droite : le canal d’arrivée, la règle qui trie le mail, sa nature, puis pour
          chaque pièce la règle qui l’a rattachée, son classement, le Drive et le cabinet.
          L’épaisseur suit le volume. Survolez ou touchez un élément pour voir son chemin et qui
          fait l’étape.
        </p>
        <DiagrammeFlux flux={d.flux} jobs={jobs} titre="Mails et pièces" />
      </section>
      <AuditDuJour d={d} />
      <section className="carte circuits-diagramme">
        <TitreCarte icone="account_balance" titre="Ce qui se passe : banque" />
        <p className="discret">
          Chaque opération relevée sur la période, par compte, et ce que le rapprochement en a fait.
        </p>
        <DiagrammeFlux
          flux={d.fluxBanque}
          jobs={jobs}
          titre="Banque"
          hauteurMax={320}
          largeurMin={560}
          largeurMax={820}
          largeurLibelleFin={240}
        />
      </section>
      <Automatismes d={d} />
    </div>
  )
}

// À regarder : ce qui attend une intervention, le plus grave d'abord.

type Point = {
  cle: string
  nombre: number
  /** Au singulier, au pluriel. */
  texte: readonly [string, string]
  grave: boolean
  lien: LinkProps['to'] | '#automatismes'
  recherche?: LinkProps['search']
}

function pointsDAudit(d: DonneesCircuits): Point[] {
  const a = d.aSuivre
  const jobs = d.automatismes.liste
  const connexions = (connexion: 'boites' | 'banques' | 'portails') => ({
    onglet: 'connexions' as const,
    connexion,
  })
  const points: Point[] = [
    {
      cle: 'echec',
      nombre: jobs.filter((j) => j.etat === 'echec').length,
      texte: ['automatisme en échec', 'automatismes en échec'],
      grave: true,
      lien: '#automatismes',
    },
    {
      cle: 'retard',
      nombre: jobs.filter((j) => j.etat === 'retard').length,
      texte: [
        'automatisme en retard : le worker tourne-t-il ?',
        'automatismes en retard : le worker tourne-t-il ?',
      ],
      grave: true,
      lien: '#automatismes',
    },
    {
      cle: 'acheminement',
      nombre: d.stock.drive.echec + d.stock.cabinet.echec,
      texte: [
        'pièce en échec vers le Drive ou le cabinet',
        'pièces en échec vers le Drive ou le cabinet',
      ],
      grave: true,
      lien: '/pieces',
    },
    {
      cle: 'boites',
      nombre: d.boites.aReconnecter,
      texte: ['boîte mail à reconnecter', 'boîtes mail à reconnecter'],
      grave: true,
      lien: '/reglages',
      recherche: connexions('boites'),
    },
    {
      cle: 'banques',
      nombre: d.banque.acces.reconnexion ?? 0,
      texte: ['accès bancaire à reconnecter', 'accès bancaires à reconnecter'],
      grave: true,
      lien: '/reglages',
      recherche: connexions('banques'),
    },
    {
      cle: 'portails-bloques',
      nombre: d.portails.connexions.bloquee ?? 0,
      texte: ['connexion de portail bloquée', 'connexions de portail bloquées'],
      grave: true,
      lien: '/reglages',
      recherche: connexions('portails'),
    },
    {
      cle: 'mails',
      nombre: (a.traiter_mail ?? 0) + (a.verifier_mail ?? 0),
      texte: ['mail à trancher', 'mails à trancher'],
      grave: false,
      lien: '/taches',
    },
    {
      cle: 'a-verifier',
      nombre: d.stock.aVerifier,
      texte: ['pièce à vérifier', 'pièces à vérifier'],
      grave: false,
      lien: '/pieces',
    },
    {
      cle: 'a-classer',
      nombre: d.stock.aClasser,
      texte: ['pièce à classer dans le plan', 'pièces à classer dans le plan'],
      grave: false,
      lien: '/pieces',
    },
    {
      cle: 'cabinet',
      nombre: d.stock.cabinet.aDecider,
      texte: ['envoi au cabinet à décider', 'envois au cabinet à décider'],
      grave: false,
      lien: '/pieces',
    },
    {
      cle: 'reglage',
      nombre: d.stock.drive.enAttente + d.stock.cabinet.enAttente,
      texte: [
        'pièce en attente d’un réglage (chemin, adresse du cabinet)',
        'pièces en attente d’un réglage (chemin, adresse du cabinet)',
      ],
      grave: false,
      lien: '/pieces',
    },
    {
      cle: 'reglement',
      nombre: a.decider_reglement ?? 0,
      texte: [
        'règlement à indiquer (société ou avance)',
        'règlements à indiquer (société ou avance)',
      ],
      grave: false,
      lien: '/taches',
    },
    {
      cle: 'payeur',
      nombre: a.indiquer_payeur ?? 0,
      texte: [
        'facture d’achat : qui a payé, à qui refacturer',
        'factures d’achat : qui a payé, à qui refacturer',
      ],
      grave: false,
      lien: '/taches',
    },
    {
      cle: 'collecteur',
      nombre: a.collecteur ?? 0,
      texte: ['collecteur qui attend un geste', 'collecteurs qui attendent un geste'],
      grave: false,
      lien: '/collecteurs',
    },
    {
      cle: 'demandes',
      nombre: a.traiter_demande ?? 0,
      texte: ['demande à traiter', 'demandes à traiter'],
      grave: false,
      lien: '/taches',
    },
    {
      cle: 'recuperer',
      nombre: a.recuperer_piece ?? 0,
      texte: ['pièce à récupérer en ligne', 'pièces à récupérer en ligne'],
      grave: false,
      lien: '/taches',
    },
    {
      cle: 'rapprochements',
      nombre: a.choisir_rapprochement ?? 0,
      texte: ['rapprochement bancaire à trancher', 'rapprochements bancaires à trancher'],
      grave: false,
      lien: '/releves',
    },
    {
      cle: 'defis',
      nombre: d.portails.defisEnAttente,
      texte: [
        'défi de portail en attente (code, CAPTCHA)',
        'défis de portail en attente (code, CAPTCHA)',
      ],
      grave: false,
      lien: '/reglages',
      recherche: connexions('portails'),
    },
    {
      cle: 'portails-verification',
      nombre:
        (d.portails.connexions.verification_requise ?? 0) + (d.portails.connexions.erreur ?? 0),
      texte: ['connexion de portail à vérifier', 'connexions de portail à vérifier'],
      grave: false,
      lien: '/reglages',
      recherche: connexions('portails'),
    },
    {
      cle: 'renouveler-banque',
      nombre: a.renouveler_banque ?? 0,
      texte: ['accès bancaire à renouveler bientôt', 'accès bancaires à renouveler bientôt'],
      grave: false,
      lien: '/reglages',
      recherche: connexions('banques'),
    },
    {
      cle: 'plans',
      nombre: a.valider_plan ?? 0,
      texte: [
        'proposition de plan de classement à valider',
        'propositions de plan de classement à valider',
      ],
      grave: false,
      lien: '/taches',
    },
  ]
  return points.filter((p) => p.nombre > 0).sort((x, y) => Number(y.grave) - Number(x.grave))
}

function AuditDuJour({ d }: { d: DonneesCircuits }) {
  const points = pointsDAudit(d)
  return (
    <section className="carte circuits-audit">
      <TitreCarte icone="front_hand" titre="Attend quelqu’un aujourd’hui" />
      {points.length === 0 && d.alertes.length === 0 ? (
        <p className="circuits-calme">
          <Icone nom="check_circle" />
          Rien n’attend d’intervention : tout tourne seul.
        </p>
      ) : (
        <ul className="circuits-points">
          {d.alertes.map((a) => (
            <li key={a.id} className="grave">
              <Icone nom="warning" />
              <Link to="/taches/$id" params={{ id: a.id }}>
                {a.titre}
              </Link>
            </li>
          ))}
          {points.map((p) => (
            <li key={p.cle} className={p.grave ? 'grave' : undefined}>
              <Icone nom={p.grave ? 'warning' : 'front_hand'} />
              {p.lien === '#automatismes' ? (
                <a href="#automatismes">
                  <strong className="mono">{p.nombre}</strong> {p.texte[p.nombre > 1 ? 1 : 0]}
                </a>
              ) : (
                <Link to={p.lien} search={p.recherche}>
                  <strong className="mono">{p.nombre}</strong> {p.texte[p.nombre > 1 ? 1 : 0]}
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// Automatismes en service : ceux que le worker a déclarés à son démarrage, par famille.

function Automatismes({ d }: { d: DonneesCircuits }) {
  const liste = d.automatismes.liste
  const parEtat = (e: EtatAutomatisme) => liste.filter((a) => a.etat === e).length
  const anomalies = parEtat('echec') + parEtat('retard')
  return (
    <details
      className="carte carte-liste circuits-automatismes"
      id="automatismes"
      open={anomalies > 0}
    >
      <summary>
        <Icone nom="schedule" />
        <strong>Automatismes en service</strong>
        <span className="mono">{liste.length}</span>
        <span className={anomalies > 0 ? 'alerte-texte' : 'discret'}>
          {anomalies > 0
            ? (['echec', 'retard'] as const)
                .filter((e) => parEtat(e) > 0)
                .map((e) => `${parEtat(e)} ${ETATS[e].libelle.toLowerCase()}`)
                .join(' · ')
            : 'tous en service'}
        </span>
      </summary>
      <p className="discret">
        {d.automatismes.demarreLe
          ? `Worker démarré ${ilYA(d.automatismes.demarreLe, d.le)}.`
          : 'Le worker n’a pas encore déclaré ses automatismes (au prochain redémarrage).'}
      </p>
      {liste.length === 0 && <Vide>Aucun automatisme déclaré.</Vide>}
      {DOMAINES_AUTOMATISME.map((domaine) => {
        const du = liste.filter((a) => a.domaine === domaine)
        if (du.length === 0) return null
        return (
          <div key={domaine} className="circuits-domaine">
            <h3 className="surtitre">{LIBELLES_DOMAINE_AUTOMATISME[domaine]}</h3>
            {du.map((a) => (
              <LigneAutomatisme key={a.nom} a={a} le={d.le} />
            ))}
          </div>
        )
      })}
    </details>
  )
}

function LigneAutomatisme({ a, le }: { a: Automatisme; le: string }) {
  const e = ETATS[a.etat]
  return (
    <div className="ligne circuits-automatisme">
      <span className={`badge grand ${e.ton}`}>
        <Icone nom={e.icone} />
        {e.libelle}
      </span>
      <div className="ligne-contenu">
        <div className="ligne-titre">
          <strong>{a.libelle}</strong>
          {a.claude && (
            <span className="badge flux-badge-claude">
              <Icone nom="auto_awesome" />
              Claude
            </span>
          )}
          <span className="mono discret">{a.nom}</span>
        </div>
        {a.role && <div className="discret">{a.role}</div>}
        {a.etat === 'echec' && a.derniereErreur && (
          <div className="alerte-texte">
            {a.echecsConsecutifs} échec{a.echecsConsecutifs > 1 ? 's' : ''} d’affilée :{' '}
            {a.derniereErreur}
          </div>
        )}
      </div>
      <div className="circuits-automatisme-quand">
        <span>{a.cadence}</span>
        <span className="discret">
          {a.derniereReussite ? `réussi ${ilYA(a.derniereReussite, le)}` : 'jamais réussi'}
        </span>
      </div>
    </div>
  )
}
